import { useMutation, useQuery } from "@tanstack/react-query";
import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import specialOnboardingIcon from "../../../assets/images/special-onboarding-icon.svg";
import CommercialAirlineNavigationBar from "../../../components/element/CommercialAirlineNavigationBar";
import Pagination from "../../../components/element/Pagination";
import SnackbarAlert from "../../../components/element/SnackbarAlert";
import { useFormData } from "../../../helpers/FormDataProvider";
import { StorageContext } from "../../../services/StorageProvider";
import useAirlineAdminApi from "../../../services/api/useAirlineAdminApi";
import { PAGINATION_PAGE_SIZE } from "../../../utils/constants";
import ROUTE_PATHS from "../../../utils/routePaths";
import "./ViewSpecialOnBoarding.css";
import SpecialOnBoardingFilter from "./Views/SpecialOnBoardingFilter";
import SpecialOnBoardingGridComponent from "./Views/SpecialOnBoardingGridComponent";
import SpecialOnBoardingSearch from "./Views/SpecialOnBoardingSearch";

const ViewSpecialOnboarding = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(() => {
    return sessionStorage.getItem("specialOnboardingCurrentPage")
      ? Number(sessionStorage.getItem("specialOnboardingCurrentPage"))
      : 0;
  });
  const [isSearching, setIsSearching] = useState(false);
  const pageSize = PAGINATION_PAGE_SIZE;
  const { userData } = useFormData();
  const { userProfileData } = useContext(StorageContext);
  const {
    searchFlightPlan,
    fetchEscortCommercialFlightPlans,
    fetchCommercialEscort,
  } = useAirlineAdminApi(userData.isAAUser);

  const [filters, setFilters] = useState({
    arrivalAirportId: "",
    departureAirportId: "",
    airlineId: "",
    departureDate: "",
    escortDepartureStatus: "",
    escortArrivalStatuses: "",
  });

  const {
    data: flightPlanData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["get-escort-commercial-flight-plans", currentPage, filters],
    queryFn: () =>
      fetchEscortCommercialFlightPlans({
        page: currentPage,
        size: pageSize,
        ...filters,
      }),
    enabled: !!userProfileData,
    retry: false,
  });

  const searchFlightPlanMutation = useMutation({
    mutationFn: ({ data }) =>
      searchFlightPlan({
        page: currentPage,
        size: pageSize,
        flightNumber: data,
      }),
  });

  const [flightPlans, setFlightPlans] = useState([]);
  const [escortStatusData, setEscortStatusData] = useState({});

  const fetchEscortDataForFlightPlans = async (flightPlans) => {
    if (!flightPlans?.data?.content) return;

    const escortDataPromises = flightPlans.data.content.map(async (item) => {
      const flightPlan = item.cfp || item;
      if (flightPlan.requiresEscortService && flightPlan.id) {
        try {
          const escortResponse = await fetchCommercialEscort(flightPlan.id);
          return {
            flightPlanId: flightPlan.id,
            arrivalStatus: escortResponse?.data?.escortService?.arrivalStatus,
            departureStatus:
              escortResponse?.data?.escortService?.departureStatus,
          };
        } catch (error) {
          console.error(
            `Error fetching escort data for flight plan ${flightPlan.id}:`,
            error
          );
          return {
            flightPlanId: flightPlan.id,
            arrivalStatus: null,
            departureStatus: null,
          };
        }
      }
      return null;
    });

    const escortDataResults = await Promise.all(escortDataPromises);
    const escortDataMap = {};

    escortDataResults.forEach((data) => {
      if (data) {
        escortDataMap[data.flightPlanId] = {
          arrivalStatus: data.arrivalStatus,
          departureStatus: data.departureStatus,
        };
      }
    });

    setEscortStatusData(escortDataMap);
  };

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(0);
    setIsSearching(false);
  };

  const handleClearFilters = () => {
    setFilters({
      arrivalAirportId: "",
      departureAirportId: "",
      airlineId: "",
      departureDate: "",
      escortDepartureStatuses: "",
      escortArrivalStatuses: "",
    });
    setCurrentPage(0);
    setIsSearching(false);
  };

  // Fetch escort data when flight plan data changes
  useEffect(() => {
    if (flightPlanData && !isSearching) {
      fetchEscortDataForFlightPlans(flightPlanData);
    }
  }, [flightPlanData, isSearching]);

  useEffect(() => {
    sessionStorage.setItem("specialOnboardingCurrentPage", currentPage);
  }, [currentPage]);

  const handleFlightClick = (item) => {
    localStorage.setItem("flightPlanData", JSON.stringify(item));
    navigate(
      ROUTE_PATHS.COMMERCIAL_AIRLINE_VIEW_SPECIAL_ONBOARDING_TRIP_DETAILS
    );
  };

  const handleNextPage = () => {
    if (currentPage + 1 < flightPlanData?.data?.totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleSearchClick = (searchText) => {
    setCurrentPage(0);
    setIsSearching(true);
    searchFlightPlanMutation.mutate({
      data: searchText,
    });
  };

  return (
    <div>
      {searchFlightPlanMutation.isError ? (
        <SnackbarAlert type={"error"}>
          {searchFlightPlanMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      <div className="view-special-onboarding-container">
        <CommercialAirlineNavigationBar
          title={"Special Onboarding"}
          navigationLogo={specialOnboardingIcon}
          backContent={
            <div className="navigation-header">
              <Link
                to={ROUTE_PATHS.COMMERCIAL_AIRLINE_HOME}
                className="navigation-header-link"
              >
                Home/
              </Link>
              Special Onboarding
            </div>
          }
        />
        <SpecialOnBoardingSearch
          isSearching={isSearching}
          setIsSearching={setIsSearching}
          onClickSearch={handleSearchClick}
        />
        <div className="view-special-onboarding-divider"></div>
        <SpecialOnBoardingFilter
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearFilters}
        />
        <div className="view-special-onboarding-divider"></div>
        <div className="view-special-onboarding-grid-header-container">
          <div className="view-special-onboarding-grid-header">All Flights</div>
          <div className="view-special-onboarding-pagination-header">{`Showing ${
            isSearching
              ? (searchFlightPlanMutation?.data?.data?.pageable?.offset ?? 0) +
                (searchFlightPlanMutation?.data?.data?.numberOfElements ?? 0)
              : (flightPlanData?.data?.pageable?.offset ?? 0) +
                (flightPlanData?.data?.numberOfElements ?? 0)
          } of ${
            isSearching
              ? searchFlightPlanMutation?.data?.data?.totalElements ?? 0
              : flightPlanData?.data?.totalElements ?? 0
          }`}</div>
        </div>
        <SpecialOnBoardingGridComponent
          flightPlanData={
            isSearching ? searchFlightPlanMutation?.data : flightPlanData
          }
          onClickCard={handleFlightClick}
          isLoading={
            isSearching ? searchFlightPlanMutation?.isPending : isLoading
          }
          error={isSearching ? searchFlightPlanMutation?.error : error}
          escortStatusData={escortStatusData}
        />
      </div>
      {flightPlanData?.data?.content &&
        flightPlanData?.data?.content.length > 0 && (
          <Pagination
            currentPage={currentPage + 1}
            totalPages={flightPlanData?.data?.totalPages}
            onPrevPage={handlePreviousPage}
            onNextPage={handleNextPage}
          />
        )}
    </div>
  );
};

export default ViewSpecialOnboarding;
